import { isDefined } from "@clipboard-health/util-ts";
import { Avatar as MuiAvatar, type SxProps, type Theme } from "@mui/material";

import { type AvatarSize } from "../../../ui-theme";
import { mergeSxProps } from "../../lib/mergeSxProps";
import { CbhIcon, type IconSize, type IconType } from "../CbhIcon";

export interface AvatarProps {
  alt?: string;
  children?: React.ReactNode;
  color?: "primary" | "secondary" | "tertiary" | "quaternary" | "septenary";
  /**
   * Fallback icon to display if src is not provided or broken.
   * Default is `user`.
   */
  fallbackIconType?: IconType;
  /**
   * Fallback text to display if src is not provided or broken.
   * Uses first letter of alt if not provided.
   */
  fallbackText?: string;
  size?: AvatarSize;
  src?: string;
  sx?: SxProps<Theme>;
}

export const iconSizeByAvatarSize: Record<AvatarSize, IconSize> = {
  small: "small",
  medium: "large",
  large: "xLarge",
};

/**
 * Avatar component.
 * For fallback content (broken or missing src) it uses the following logic:
 * 1. If children are provided, use them as fallback.
 * 2. If fallbackText is provided, use it as fallback.
 * 3. If alt is provided, use the first letter of alt as fallback.
 * 4. If fallbackIconType is provided, use it as fallback.
 * 5. If none of the above are provided, use the default fallback icon (CbhIcon type="user").
 */
export function Avatar(props: AvatarProps) {
  const {
    alt,
    sx,
    size = "medium",
    children,
    src,
    color = "primary",
    fallbackIconType = "user",
    fallbackText = alt?.[0],
    ...restProps
  } = props;

  const getFallbackContent = (): React.ReactNode => {
    if (isDefined(children)) {
      return children;
    }

    // we want to check for empty strings too
    if (fallbackText) {
      return fallbackText;
    }

    return <CbhIcon type={fallbackIconType} size={iconSizeByAvatarSize[size]} />;
  };

  return (
    <MuiAvatar
      alt={alt}
      size={size}
      src={src}
      sx={mergeSxProps(
        (theme) => ({
          backgroundColor: theme.palette.components?.avatar?.[color],
        }),
        sx
      )}
      {...restProps}
    >
      {getFallbackContent()}
    </MuiAvatar>
  );
}
